import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import LocationThreeAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-three/location-three.entity";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  noop,
  Normalizers,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { ICreateLocationThree } from "./interfaces/create-location-three.interface";
import { IUpdateLocationThree } from "./interfaces/update-location-three.interface";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import LocationOneAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-one/location-one.entity";
import LocationTwoAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-two/location-two.entity";

const apiUrl = env.API_LOCATION_URL;
const moduleName = "location_three";

@Injectable({
  providedIn: "root",
})
export class LocationThreeService extends AbstractService<
  LocationThreeAttributes,
  ICreateLocationThree,
  IUpdateLocationThree
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @param locationOneCodeList location-one codes filter
   * @param locationTwoCodeList location-two codes filter
   * @param selectedStatuses selected statuses filter
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<LocationThreeAttributes>,
    locationOneCodeList: string[],
    locationTwoCodeList: string[],
    selectedStatuses: number[]
  ): Observable<IPaginationResult<LocationThreeAttributes>> {
    
    const query = buildQuery<LocationThreeAttributes>(
      andWhere('_deleted', Ops.not(true)),
      options.search ? andWhere("code", Ops.like(options.search ?? "", "i")) : noop(),
      options.search ? orWhere("name", Ops.like(options.search ?? "", "i")) : noop(),
      locationOneCodeList && locationOneCodeList.length > 0 ? where('locationOne.code' as keyof LocationThreeAttributes, Ops.in(...locationOneCodeList)) : noop(),
      locationTwoCodeList && locationTwoCodeList.length > 0 ? where('locationTwo.code' as keyof LocationThreeAttributes, Ops.in(...locationTwoCodeList)) : noop(),
      selectedStatuses && selectedStatuses.length > 0 ? where('statusId' as keyof LocationThreeAttributes, Ops.in(...selectedStatuses)) : noop(),
      orderBy(
        options.sort ?? "code",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LocationThreeAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(locationTwo?): Observable<IRequestResult<LocationThreeAttributes[]>> {
    const query = buildQuery<LocationThreeAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      locationTwo ? andWhere('locationTwo.code' as keyof LocationThreeAttributes, Ops.eq(locationTwo)) : noop(),
      select(["code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<LocationThreeAttributes[]>>(API_URL);
  }
}
